import { EyeOutlined } from '@ant-design/icons';
import {
  Button,
  Card, Col,
  message,
  Row,
  Space, Spin,
  Table,
  TableProps,
  Tooltip,
  Typography
} from 'antd';
import { AxiosError } from 'axios';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useQuery
} from 'react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';

import InfoCard from './components/InfoCard';

import { useAppSelector } from 'app/store';
import HeaderPage from 'common/components/HeaderPage';
import {
  getProjectByIdService
} from 'common/services/extends/project';
import { ROUTE_PATHS } from 'common/utils/constant';
import { detectError } from 'common/utils/functions';
import roles, { getPermission } from 'configs/roles';

interface DataType {
  name: string;
  projectId: string;
  staffId: string;
}

const SalaryCalculateDetailInfo: React.FC<ActiveRoles> = () => {
  /* Hooks */
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const projectId = searchParams.get('projectId') || '';

  const rolesUser = useAppSelector((state) => state.auth.roles);

  /* States */

  const queryKey = ['project-detail', projectId];

  const { isLoading, data: projectData } = useQuery(
    queryKey,
    () => getProjectByIdService(projectId),
    {
      enabled: !!projectId,
      onError: (error: unknown) => {
        if (error instanceof AxiosError) {
          const status = error.response?.status;
          message.error(t(detectError(status)));
        }
      }
    }
  );

  const columns: TableProps<DataType>['columns'] = [
    {
      title: t('system.name'),
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: t('system.action'),
      key: 'action',
      width: 100,
      align: 'center',
      render: (data: DataType) => (
        <Space>
          <div className="t-pagetable_wrap">
            <Tooltip title={t('system.viewDetail')}>
              <Button
                disabled={!getPermission(rolesUser, roles.SHOW_PAYROLL_PROJECT)}
                icon={<EyeOutlined />}
                onClick={() => navigate(`${ROUTE_PATHS.SALARY_CALCULATE_PERIOD_DETAIL}?projectId=${data.projectId}&staffFullTimeId=${data.staffId}`)}
              />
            </Tooltip>
          </div>
        </Space>
      ),
    },
  ];

  const data = useMemo(() => {
    if (projectData) {
      return projectData.payrolls?.map((item) => ({
        name: item.fullname || '',
        projectId: projectId?.toString(),
        staffId: item.id?.toString(),
      }));
    }

    return [];
  }, [projectData, projectId]);

  /* Render */
  return (
    <div className="p-programDetail">
      <HeaderPage
        fixed
        title={t('salary.programDetail')}
      />
      <Spin size="large" spinning={isLoading}>
        <div className="t-mainlayout_wrapper">
          <Row gutter={16}>
            <Col>
              <Space direction="vertical" size={24} style={{ width: '100%' }}>
                <InfoCard />
                <Card
                  title={(
                    <Typography.Title level={4}>
                      {t('program.creatorTableSalary')}
                    </Typography.Title>
                  )}
                >
                  <Table<DataType>
                    columns={columns}
                    dataSource={data || []}
                    pagination={false}
                  />
                </Card>
              </Space>
            </Col>
          </Row>
        </div>
      </Spin>
    </div>
  );
};

export default SalaryCalculateDetailInfo;
